import * as fb from '@/firebase';
import {
  AbstractItemModel,
  ItemType
} from '@/models/items/abstract-item-model';
import { ListModelUtil } from '@/model-utils/list-model-util';
import { ListModel, ListItemEntry } from '@/models/list-model';
import { GenericItem } from '@/models/items/generic-item-model';
import { ProductItem } from '@/models/items/product-item-model';
import { PlaceItem } from '@/models/items/place-item-model';
import { MediaItem } from '@/models/items/media-item-model';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { UserProfile } from '@/models/user-profile';

import { GENIUS_TS_ID } from '@/secrets';

export class ItemModelUtil {
  public static async hydrateItems(
    itemIds: string[]
  ): Promise<AbstractItemModel[]> {
    const docPromises = itemIds.map((id: string) =>
      fb.itemsCollection.doc(id).get()
    );
    const docs = await Promise.all(docPromises);
    return docs.filter((itemDoc) => {
      if (!itemDoc.data()) {
        console.error('missing itemId', itemDoc.id);
      }
      return !!itemDoc.data();
    }).map((itemDoc) => {
      return ItemModelUtil.itemDocToDomainModel(itemDoc.id, itemDoc.data());
    });
  }

  public static deleteItem({
    items,
    item,
    listId
  }: {
    items: AbstractItemModel[];
    item: AbstractItemModel;
    listId: string;
  }): Promise<any> {
    const promise1 = ListModelUtil.deleteItemFromList({ items, item, listId });
    const promise2 = fb.itemsCollection.doc(item.id).delete();
    return Promise.all([promise1, promise2]);
  }

  public static addItemToList(
    item: AbstractItemModel,
    list: ListModel,
    user: UserProfile
  ): Promise<AbstractItemModel> {
    const newItemRef = fb.itemsCollection.doc();
    item.listId = list.id;
    item.listTitle = list.title;
    item.authorId = user.id;

    const promise1 = newItemRef.set(item.toFirebaseDoc()).catch((error) => {
      alert('Error adding document: ' + error);
    });

    const listDocRef = fb.listsCollection.doc(list.id);
    return fb.firestore
      .runTransaction((transaction) => {
        // This code may get re-run multiple times if there are conflicts.
        return transaction.get(listDocRef).then((listDoc) => {
          if (!listDoc.exists) {
            throw new Error('Document does not exist!');
          }

          const transactionList = ListModel.fromFirebaseDoc(listDoc.id, listDoc.data());

          const newEntryList = [
            ...(transactionList.itemEntries || []),
            ListItemEntry.fromItemId(newItemRef.id)
          ];

          const newImages = [...(transactionList.images || [])];
          if (item.imageURL) {
            newImages.push(item.imageURL);
          }

          const updateData =  {
            images: newImages,
            itemEntries: newEntryList.map((e) => e.toFirebaseDoc()),
            timeLastUpdated: new Date().getTime()
          };
          transaction.set(newItemRef, item.toFirebaseDoc());
          transaction.update(listDocRef, updateData);
        });
      })
      .catch(function(err) {
        // This will be an "population is too big" error.
        console.error(err);
      }).then(() => {
      item.id = newItemRef.id;
      return item;
    });
  }

  public static updateItem(item: AbstractItemModel) {
    return fb.itemsCollection.doc(item.id).set(item.toFirebaseDoc());
  }

  public static itemDocToDomainModel(
    id: string,
    document: any
  ): AbstractItemModel {
    if (document.type === ItemType.Generic.toString()) {
      return new GenericItem().fromFirebaseDoc(id, document);
    } else if (document.type === ItemType.Product.toString()) {
      return new ProductItem().fromFirebaseDoc(id, document);
    } else if (document.type === ItemType.Place.toString()) {
      return new PlaceItem().fromFirebaseDoc(id, document);
    } else if (document.type === ItemType.Media.toString()) {
      return new MediaItem().fromFirebaseDoc(id, document);
    }
    throw new Error('could not deserialize: ' + document.type);
  }

  public static getLinkDomain(item: AbstractItemModel): string {
    try {
      let url = item.url;
      if (url.indexOf('http') === -1) {
        url = 'http://' + url;
      }
      return new URL(url).hostname.replace('www.', '');
    } catch {
      console.error('invalid url ', item.url);
      return null;
    }
  }

  public static getWrappedLink(item: AbstractItemModel): string {
    if (item.url) {
      if (
        this.getLinkDomain(item).indexOf('amazon.') > -1 ||
        this.getLinkDomain(item).indexOf('play.google.com') > -1 ||
        this.getLinkDomain(item).indexOf('itunes.apple.com') > -1
      ) {
        return `http://buy.geni.us/Proxy.ashx?tsid=${GENIUS_TS_ID}&GR_URL=${escape(
          item.url
        )}`;
      } else {
        return item.url;
      }
    }
    return null;
  }

  public static singleAddressLine(item: PlaceItem): string {
    return [
      item.addressLine1,
      item.addressLine2,
      item.city,
      item.state,
      item.zip
    ]
      .filter((i) => !!i)
      .join(', ');
  }

  public static formattedPhoneNumber(item: PlaceItem): string {
    if (!item.phonenumber) {
      return '';
    }
    const phoneNumber = parsePhoneNumberFromString(item.phonenumber);
    if (!phoneNumber) {
      return item.phonenumber;
    }
    return phoneNumber.formatNational();
  }

  public static phoneNumberLink(item: PlaceItem): string {
    if (!item.phonenumber) {
      return '';
    }
    const phoneNumber = parsePhoneNumberFromString(item.phonenumber);
    if (!phoneNumber) {
      return 'tel:' + item.phonenumber;
    }
    return phoneNumber.getURI();
  }
}
